"use client"
import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import PortableTextRenderer from "@/app/(site)/_components/Atoms/PortableTextRenderer/PortableTextRenderer"
import StoryText from "@/app/(site)/_components/Atoms/Storytext"
import { type Content } from "@/app/(site)/_components/ContentMapper"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { type ImageType, type TitledGeneralLink } from "@/types"
import type { ColorValue } from "@sanity/color-input"
import type { PortableTextBlock } from "sanity"
import { getGeneralLinkHref } from "@/src/utils/links/getGeneralLinkHref"
import { clsx } from "@/src/utils/clsx"

export interface FeatureBlockData extends Content {
    heading: string
    portableTextParagraph?: PortableTextBlock[]
    links: TitledGeneralLink[]
    image: ImageType
    hasWhiteText: boolean
    backgroundColor: ColorValue
}

type FeatureProps = {
    data?: FeatureBlockData
    isAboveTheFold?: boolean
}
export default function Feature({ data, isAboveTheFold }: FeatureProps) {
    if (!data) return null
    const { heading, image, links, hasWhiteText, backgroundColor } = data
    const textColorClass = hasWhiteText ? "text-white" : "text-main-black"
    return (
        <section className="py-32 md:py-56">
            <div
                className={`overflow-hidden text-main-black md:h-[calc(100vh-150px)] ${textColorClass}`}
                style={{
                    backgroundColor: backgroundColor?.hex
                }}
            >
                <div className="grid h-full grid-cols-1 gap-24 p-16 md:grid-flow-row-dense md:grid-cols-2 md:p-24">
                    <div className="col-span-1 flex h-full flex-col justify-between">
                        <div className="grid grid-cols-1 gap-24 md:grid-cols-2">
                            <StoryText size="size1">{heading}</StoryText>
                            {!!data.portableTextParagraph && (
                                <div className={clsx("md:mb-0", textColorClass)}>
                                    <PortableTextRenderer
                                        content={data.portableTextParagraph}
                                        render={{
                                            block: {
                                                normal: text => (
                                                    <StoryText size="size4">
                                                        {text}
                                                    </StoryText>
                                                )
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <div
                            className={`flex gap-24 uppercase md:flex ${textColorClass}`}
                        >
                            {links?.map((link, index) =>
                                link.external ? (
                                    <a
                                        key={index}
                                        href={link.external}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <div className="flex items-center gap-8 font-trimRegularNew">
                                            {link.title}
                                            <IconArrowRight className="h-20 w-20" />
                                        </div>
                                    </a>
                                ) : (
                                    <DynamicLink
                                        key={index}
                                        href={getGeneralLinkHref(link) ?? ""}
                                        prefetch={false}
                                    >
                                        <div
                                            key={index}
                                            className="flex items-center gap-8 font-trimRegularNew"
                                        >
                                            {link.title}
                                            <IconArrowRight className="h-20 w-20" />
                                        </div>
                                    </DynamicLink>
                                )
                            )}
                        </div>
                    </div>
                    <div
                        style={{ position: "relative" }}
                        className="relative order-first col-span-1 mb-0 block aspect-square w-full md:order-last md:m-0 md:mb-0 md:aspect-auto"
                    >
                        <BaseImage
                            src={image?.asset._ref || ""}
                            alt={image?.alt || ""}
                            fill
                            mimeType={image?.asset?.mimeType || ""}
                            isAboveTheFold={isAboveTheFold}
                            className="object-cover"
                            sizes="(max-width: 968px) 60vw, 50vw"
                        />
                    </div>
                </div>
            </div>
        </section>
    )
}
